import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiClock, FiArrowRight, FiCheckCircle, FiZoomIn, FiSearch,
  FiFilter, FiUsers, FiFolder, FiStar
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const TimelineDocs = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/timeline" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Timeline / Gantt View</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-orange-100 dark:bg-orange-900/30 rounded-xl mb-4">
              <FiClock className="w-6 h-6 text-orange-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Timeline / Gantt View</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Visualize your tasks and projects across time with an interactive Gantt-style timeline. 
              Plan, track, and manage schedules with day, week, and month views.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Project Planning at a Glance</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                The Timeline view gives you a bird's-eye view of all your tasks positioned on a time axis. 
                See exactly when work starts, how long it takes, and how tasks overlap — helping you plan 
                better and avoid scheduling conflicts.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">3</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">View Modes</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">3</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Grouping Options</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">2</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Workspaces Supported</div>
                </div>
              </div>
            </div>
          </section>

          {/* View Modes */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">View Modes</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FiClock className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Day View</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Granular hour-by-hour layout for detailed short-term planning. 
                  Perfect for daily standups and sprint reviews.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FiClock className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Week View</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Columns for each day of the week. Ideal for sprint planning 
                  and tracking weekly deliverables.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <FiClock className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Month View</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Calendar-style overview for quarterly planning and tracking 
                  long-running projects and milestones.
                </p>
              </div>
            </div>
          </section>

          {/* Grouping */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Task Grouping</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Group tasks on the timeline to organize your view based on what matters most to you.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiFolder className="w-5 h-5 text-blue-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">By Project</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">See tasks grouped under their parent project</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiStar className="w-5 h-5 text-amber-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">By Status</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Group by To Do, In Progress, Done, etc.</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUsers className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">By Assignee</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">View workload distribution across team members</p>
              </div>
            </div>
          </section>

          {/* Controls */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Navigation & Controls</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiZoomIn className="w-5 h-5 text-indigo-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Zoom Controls</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Zoom in for detail or out for a broader view of your schedule</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiSearch className="w-5 h-5 text-indigo-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Search</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Find specific tasks quickly within the timeline</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiFilter className="w-5 h-5 text-indigo-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Filters</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Filter by project, status, priority, or assignee</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiClock className="w-5 h-5 text-indigo-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Today Marker</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">A vertical indicator highlights today's date at all times</p>
              </div>
            </div>
          </section>

          {/* How to Access */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How to Access</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <ol className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                  <span><strong>Personal workspace:</strong> Navigate to <strong>Tasks → Timeline</strong> from the sidebar</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                  <span><strong>Organization workspace:</strong> Navigate to <strong>Tasks → Timeline</strong> within your organization</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                  <span>Select your preferred view mode (Day, Week, or Month)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                  <span>Use grouping, filters, and search to customize your view</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Tips */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Pro Tips</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Set Start & Due Dates</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Tasks need both start and due dates to appear on the timeline</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Use Month View for Planning</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Month view gives the best overview for quarterly project planning</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Group by Assignee</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Quickly identify overloaded team members with assignee grouping</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Dark Mode Friendly</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">The timeline fully supports dark mode with optimized contrast</p>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/dependencies"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Dependencies</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/features/views"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Views & Boards</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: June 2025</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default TimelineDocs;
